import { z } from 'zod';
import { gameVariableSchema, projectItemSchema, } from '@/entities/project/model/project';
import { parseProjectDocument, projectSettingsSchema, } from '@/entities/project/model/projectDocument';
import { httpClient } from '@/shared/api/httpClient';
import { readApiEnvelopeData } from '@/shared/api/response';
/** 创建项目响应结构，与后端 CreateProjectVO 对齐。 */
const createProjectResponseSchema = projectItemSchema;
/**
 * 解析后端返回的设置响应：settings 字段先以 unknown 接收，
 * 再经 projectSettingsSchema 完成当前协议的 schema 校验。
 */
/**
 * 读出 settings 载荷。个别历史记录把 JSON 再包成字符串，先解开再校验。
 * @param settings 接口里的 settings 字段
 * @returns 对象或原值
 */
function readStoredSettings(settings) {
    if (typeof settings !== 'string')
        return settings;
    try {
        return JSON.parse(settings);
    }
    catch {
        return settings;
    }
}
function parseProjectSettingsResponse(raw) {
    const base = z
        .object({ projectId: z.coerce.number(), settings: z.unknown().nullable() })
        .parse(raw);
    if (base.settings == null) {
        return { projectId: base.projectId, settings: null };
    }
    /** 已解开的设置对象；不是对象时无法保留字段，交给调用方按缺省设置打开。 */
    const stored = readStoredSettings(base.settings);
    const parsed = projectSettingsSchema.safeParse(stored);
    return {
        projectId: base.projectId,
        settings: parsed.success ? parsed.data : null,
    };
}
/**
 * 解析后端返回的文档响应：document 字段先以 unknown 接收，
 * 再经 parseProjectDocument 完成当前协议的 schema 校验；
 * 所有从外部进入工作区的原始文档统一由此入口。
 */
function parseProjectDocumentResponse(raw) {
    const base = z.object({ projectId: z.number(), document: z.unknown().nullable() }).parse(raw);
    return {
        projectId: base.projectId,
        document: base.document === null ? null : parseProjectDocument(base.document),
    };
}
/** 项目列表查询的缓存键，小规模页面直接使用 API 时复用。 */
export const PROJECT_LIST_QUERY_KEY = ['projects'];
/** 回收站列表查询的缓存键，小规模页面直接使用 API 时复用。 */
export const PROJECT_RECYCLE_LIST_QUERY_KEY = ['projects', 'recycled'];
/** 项目创作初始化查询缓存键。 */
export function projectCreationFlowQueryKey(projectId) {
    return ['project-creation-flow', projectId];
}
/** 生成指定项目的游戏变量查询缓存键。 */
export function projectVariablesQueryKey(projectId) {
    return ['project-variables', projectId];
}
/** 为当前登录用户创建影游项目，需登录态。 */
export async function createProject(input) {
    const response = await httpClient.post('/project/create', input);
    return createProjectResponseSchema.parse(readApiEnvelopeData(response));
}
const creationPlanSchema = z.object({
    id: z.number(),
    projectId: z.number(),
    planCode: z.string(),
    contentJson: z.string(),
    status: z.number().int(),
    errorMessage: z.string().nullable().optional(),
    taskId: z.number().nullable().optional(),
    isSelected: z.number().int().nullable().optional(),
    createTime: z
        .union([z.string(), z.number(), z.array(z.number())])
        .nullable()
        .optional(),
});
const aiGenerationTaskSchema = z.object({
    id: z.number(),
    projectId: z.number(),
    billingTaskId: z.string().nullable().optional(),
    scriptId: z.number().nullable().optional(),
    planId: z.number().nullable().optional(),
    taskType: z.string(),
    status: z.number().int(),
    promptKey: z.string(),
    modelName: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    outputJson: z.string().nullable().optional(),
    inputJson: z.string().nullable().optional(),
    createTime: z
        .union([z.string(), z.number(), z.array(z.number())])
        .nullable()
        .optional(),
    startTime: z
        .union([z.string(), z.number(), z.array(z.number())])
        .nullable()
        .optional(),
    finishTime: z
        .union([z.string(), z.number(), z.array(z.number())])
        .nullable()
        .optional(),
});
const creationFlowSchema = z.object({
    projectId: z.number(),
    creationMode: z.enum(['inspiration', 'non_standard', 'standard', 'blank']).nullable().optional(),
    creationStatus: z.number().int(),
    selectedPlanId: z.number().nullable().optional(),
    creationError: z.string().nullable().optional(),
    plans: z.array(creationPlanSchema),
    tasks: z.array(aiGenerationTaskSchema),
});
export async function fetchProjectCreationFlow(projectId) {
    const response = await httpClient.get('/project/creation-flow', { params: { id: projectId } });
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function configureProjectCreation(input) {
    const response = await httpClient.post('/project/creation-flow/configure', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function runProjectCreationTask(taskId) {
    const response = await httpClient.post(`/project/creation-flow/task/${String(taskId)}/run`);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function cancelProjectCreationTask(projectId) {
    const response = await httpClient.post('/project/creation-flow/task/cancel', { id: projectId });
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function selectProjectCreationPlan(input) {
    const response = await httpClient.post('/project/creation-flow/plan/select', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function regenerateProjectCreationPlans(input) {
    const response = await httpClient.post('/project/creation-flow/plan/regenerate', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function extractProjectAssets(projectId) {
    const response = await httpClient.post('/project/creation-flow/assets/extract', {
        id: projectId,
    });
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
/**
 * 按项目发起分镜拆分任务。
 *
 * 与素材拆分对称：一个项目一个任务，后台逐节点调用大模型写视频提示词；
 * 资产拆分成功后会自动发起，本入口用于失败后手动重跑。
 */
export async function splitProjectStoryboards(projectId) {
    const response = await httpClient.post('/project/creation-flow/storyboards/split', {
        id: projectId,
    });
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function generateProjectChapterScript(input) {
    const response = await httpClient.post('/project/creation-flow/chapter/generate', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
export async function consumeProjectChapterScriptDraft(projectId, taskId) {
    await httpClient.post(`/project/creation-flow/chapter/generate/${String(taskId)}/consume`, {
        id: projectId,
    });
}
export async function uploadProjectScript(projectId, file, sourceMode) {
    const form = new FormData();
    form.append('file', file);
    if (sourceMode)
        form.append('sourceMode', sourceMode);
    await httpClient.post(`/project/${String(projectId)}/script/upload`, form);
}
/** 查询当前登录用户的项目列表，按更新时间倒序。 */
export async function fetchProjects() {
    const response = await httpClient.get('/project/list');
    return projectItemSchema.array().parse(readApiEnvelopeData(response));
}
/** 编辑项目基础信息，仅限项目归属人。 */
export async function updateProject(input) {
    const response = await httpClient.post('/project/update', input);
    return createProjectResponseSchema.parse(readApiEnvelopeData(response));
}
/** 查询当前登录用户所拥有项目的游戏变量列表。 */
export async function fetchProjectVariables(projectId) {
    const response = await httpClient.get(`/project/${String(projectId)}/variables`);
    return gameVariableSchema.array().parse(readApiEnvelopeData(response));
}
/** 向当前项目追加一个游戏变量，返回更新后的完整列表。 */
export async function addProjectVariable(projectId, variable) {
    const response = await httpClient.post(`/project/${String(projectId)}/variables`, variable);
    return gameVariableSchema.array().parse(readApiEnvelopeData(response));
}
/** 按名称删除当前项目中的一个游戏变量，返回更新后的完整列表。 */
export async function deleteProjectVariable(projectId, variableName) {
    const response = await httpClient.delete(`/project/${String(projectId)}/variables/${encodeURIComponent(variableName)}`);
    return gameVariableSchema.array().parse(readApiEnvelopeData(response));
}
/** 查询项目播放文档：由后端从剧情树正式表拼装，供创作者预览使用。 */
export async function fetchProjectPlayDocument(id) {
    const response = await httpClient.get('/project/play-document', { params: { id } });
    return parseProjectDocumentResponse(readApiEnvelopeData(response));
}
/** 查询项目运行设置，独立存储于 cg_project.settings_json。 */
export async function fetchProjectSettings(id) {
    const response = await httpClient.get('/project/settings', { params: { id } });
    return parseProjectSettingsResponse(readApiEnvelopeData(response));
}
/** 保存项目运行设置，整体覆盖 settings_json。 */
export async function saveProjectSettings(input) {
    const response = await httpClient.post('/project/settings/save', input);
    return parseProjectSettingsResponse(readApiEnvelopeData(response));
}
/** 删除项目：软删除移入回收站。 */
export async function deleteProject(id) {
    await httpClient.post('/project/delete', { id });
}
/**
 * 复制项目：以源项目基础信息新建副本（名称追加「副本」后缀）。
 * 剧本、剧情树与素材等关联数据不随复制。
 * @param id 源项目 ID。
 * @returns 副本项目 ID。
 */
export async function copyProject(id) {
    const response = await httpClient.post('/project/copy', { id });
    return z.number().parse(readApiEnvelopeData(response));
}
/** 查询当前登录用户回收站中的项目列表，按移入时间倒序。 */
export async function fetchRecycledProjects() {
    const response = await httpClient.get('/project/recycle-list');
    return projectItemSchema.array().parse(readApiEnvelopeData(response));
}
/** 从回收站恢复项目。 */
export async function restoreProject(id) {
    await httpClient.post('/project/restore', { id });
}
/** 彻底删除回收站中的项目，不可恢复。 */
export async function destroyProject(id) {
    await httpClient.post('/project/destroy', { id });
}
/** 回收站批量彻底删除结果：成功数量与被跳过项目的说明。 */
const recycleDestroyResultSchema = z.object({
    destroyedCount: z.number().int(),
    skippedReasons: z.array(z.string()),
});
/** 批量恢复回收站中的项目（整批事务，任一条失败则全部不恢复）。 */
export async function restoreRecycledProjects(ids) {
    await httpClient.post('/project/restore-batch', { ids });
}
/** 批量彻底删除回收站中的项目，返回成功数量与被跳过项目的说明。 */
export async function destroyRecycledProjects(ids) {
    const response = await httpClient.post('/project/destroy-batch', { ids });
    return recycleDestroyResultSchema.parse(readApiEnvelopeData(response));
}
/** 清空回收站：彻底删除全部项目，返回成功数量与被跳过项目的说明。 */
export async function clearProjectRecycleBin() {
    const response = await httpClient.post('/project/destroy-all');
    return recycleDestroyResultSchema.parse(readApiEnvelopeData(response));
}
/** 灵感生成前创建隐藏草稿，同一请求键返回同一个项目 ID。 */
export async function createProjectDraft(input) {
    const response = await httpClient.post('/project/creation-draft/create', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
/** 点击下一步提交完整配置并转正式；不会创建第二个项目。 */
export async function submitProjectDraft(input) {
    const response = await httpClient.post('/project/creation-draft/submit', input);
    return creationFlowSchema.parse(readApiEnvelopeData(response));
}
